import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Users, Feather, Sparkles } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/39141d99-ef4f-4686-a207-2702e1fdeb56/images/976fe523e979a7a74446917beadc12695acf024ad503eb9deebae737e3d9e41e.png";
const LEARNING_IMG = "https://static.prod-images.emergentagent.com/jobs/39141d99-ef4f-4686-a207-2702e1fdeb56/images/bce79ad3fcce37e2fe99bdfb1e340c364faf691899f2a686a9ca08e727d130ee.png";
const PARCHMENT_IMG = "https://static.prod-images.emergentagent.com/jobs/39141d99-ef4f-4686-a207-2702e1fdeb56/images/71961a88075b238f2c3ac01d6af1c6efc499931b1c198a38074b5530f81c88c1.png";

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="tag" data-testid="hero-tag">
              <Sparkles size={14} /> A sabhā for seekers
            </span>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-medium text-[#2D3B31] mt-6">
              Study the Gītā,
              <br />
              together, in <em className="text-[#D96C4A] not-italic">stillness.</em>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#5C6B5F] font-light">
              Eighteen chapters. Countless questions. One shared silence between them.
              Read the shlokas, reflect upon their meaning, and converse with fellow learners
              in a community moderated with care.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/chapters" className="btn-accent inline-flex items-center gap-2" data-testid="hero-browse-chapters">
                Enter the chapters <ArrowRight size={18} />
              </Link>
              <Link to="/register" className="btn-outline" data-testid="hero-join-btn">
                Join the sabhā
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <div className="font-serif-display text-3xl text-[#2D3B31]">18</div>
                <div className="text-xs uppercase tracking-widest text-[#8A948C] mt-1">Chapters</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl text-[#2D3B31]">700+</div>
                <div className="text-xs uppercase tracking-widest text-[#8A948C] mt-1">Shlokas</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl text-[#2D3B31]">∞</div>
                <div className="text-xs uppercase tracking-widest text-[#8A948C] mt-1">Reflections</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up animate-delay-2">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[2rem] bg-[#F2D8CF] opacity-40 blur-2xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-[#EAE5D9] aspect-square">
                <img src={HERO_IMG} alt="Cosmic mandala" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 organic-card p-5 max-w-[240px] hidden md:block">
                <div className="shloka text-[#2D3B31] text-lg">
                  योगः कर्मसु कौशलम्
                </div>
                <div className="text-xs text-[#8A948C] mt-2 tracking-wide">Gītā 2.50</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED SHLOKA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          className="relative rounded-[2rem] overflow-hidden border p-10 md:p-16 text-center"
          style={{ borderColor: "#EAE5D9", backgroundImage: `url(${PARCHMENT_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="absolute inset-0 bg-white/75" />
          <div className="relative">
            <div className="text-xs tracking-widest uppercase text-[#D96C4A] mb-4">Chapter 2.47 — Karma Yoga</div>
            <p className="shloka text-2xl md:text-3xl text-[#2D3B31]">
              कर्मण्येवाधिकारस्ते मा फलेषु कदाचन।{'\n'}
              मा कर्मफलहेतुर्भूर्मा ते सङ्गोऽस्त्वकर्मणि॥
            </p>
            <p className="mt-6 max-w-2xl mx-auto text-base text-[#5C6B5F] italic font-serif-display">
              "You have the right to action alone, never to its fruits.
              Let not the fruits be your motive, nor cling to inaction."
            </p>
          </div>
        </div>
      </section>

      {/* THREE PATHS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="grid md:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="md:col-span-5">
            <div className="text-xs tracking-widest uppercase text-[#8A948C]">The three practices</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tight font-medium text-[#2D3B31] mt-3">
              Read, reflect, and respond — with gentleness.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#5C6B5F] font-light max-w-md">
              The Gītā rewards slow reading. This sabhā is built so you can sit with a shloka,
              hold a question, and share it when you're ready.
            </p>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
            <PathCard
              icon={<BookOpen size={28} color="#D96C4A" />}
              title="Learn"
              desc="All 18 chapters with Sanskrit shlokas, transliteration, and faithful English translations."
              testid="path-card-learn"
            />
            <PathCard
              icon={<Feather size={28} color="#D96C4A" />}
              title="Reflect"
              desc="Pen your interpretation. Start a thread in any chapter. Return to it often."
              testid="path-card-reflect"
            />
            <PathCard
              icon={<Users size={28} color="#D96C4A" />}
              title="Discuss"
              desc="Reply with kindness. Upvote what resonates. Report anything that disturbs harmony."
              testid="path-card-discuss"
            />
            <PathCard
              icon={<Sparkles size={28} color="#D96C4A" />}
              title="Harmony"
              desc="Moderators watch over the sabhā, removing noise so truth may speak."
              testid="path-card-harmony"
            />
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="rounded-[2rem] overflow-hidden border" style={{ borderColor: "#EAE5D9" }}>
          <div className="grid md:grid-cols-2">
            <div className="aspect-[4/3] md:aspect-auto">
              <img src={LEARNING_IMG} alt="A serene study space" className="w-full h-full object-cover" />
            </div>
            <div className="p-10 md:p-14 flex flex-col justify-center bg-white">
              <div className="text-xs tracking-widest uppercase text-[#D96C4A]">Moderated with care</div>
              <h3 className="font-serif-display text-3xl sm:text-4xl font-medium text-[#2D3B31] mt-3">
                A sabhā, not a shouting square.
              </h3>
              <p className="mt-5 text-[#5C6B5F] leading-relaxed">
                Every thread and reply can be gently reported. Our moderators review,
                remove what does not serve, and — when needed — pause voices that disturb
                the peace. The doors remain open. The silence remains sacred.
              </p>
              <div className="mt-8">
                <Link to="/register" className="btn-accent inline-flex items-center gap-2" data-testid="community-join-btn">
                  Begin your journey <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PathCard({ icon, title, desc, testid }) {
  return (
    <div className="organic-card p-8" data-testid={testid}>
      <div className="mb-4">{icon}</div>
      <div className="font-serif-display text-2xl text-[#2D3B31]">{title}</div>
      <p className="text-sm text-[#5C6B5F] leading-relaxed mt-2">{desc}</p>
    </div>
  );
}
